import React, { useState, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import getCroppedImg from '../utils/cropUtils'
import { X, Check, ZoomIn, ZoomOut, RotateCw } from 'lucide-react'

const ImageCropper = ({ imageSrc, onCropComplete, onCancel }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [rotation, setRotation] = useState(0)
    const [zoom, setZoom] = useState(1)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)

    const onCropChange = (crop) => {
        setCrop(crop)
    }

    const onRotationChange = (rotation) => {
        setRotation(rotation)
    }

    const onZoomChange = (zoom) => {
        setZoom(zoom)
    }

    const onCropCompleteHandler = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels)
    }, [])

    const handleSave = async () => {
        try {
            const croppedImage = await getCroppedImg(
                imageSrc,
                croppedAreaPixels,
                rotation
            )
            onCropComplete(croppedImage)
        } catch (e) {
            console.error(e)
        }
    }

    return (
        <div className="fixed inset-0 bg-black/90 z-[1000] flex flex-col animate-in fade-in duration-300">
            <div className="relative flex-1 bg-[#1A1F2B] overflow-hidden">
                <Cropper
                    image={imageSrc}
                    crop={crop}
                    rotation={rotation}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onRotationChange={setRotation}
                    onCropComplete={onCropCompleteHandler}
                    onZoomChange={setZoom}
                    classes={{
                        containerClassName: "bg-[#1A1F2B]",
                        mediaClassName: "",
                        cropAreaClassName: "border-2 border-indigo-500 shadow-[0_0_0_9999px_rgba(0,0,0,0.7)]"
                    }}
                />
            </div>

            <div className="bg-white dark:bg-[#13161C] border-t border-slate-200 dark:border-white/10 p-6 flex flex-col gap-4">
                <div className="flex items-center gap-6 max-w-lg mx-auto w-full">
                    <div className="flex items-center gap-3 flex-1">
                        <ZoomOut size={16} className="text-slate-500" />
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e) => setZoom(e.target.value)}
                            className="flex-1 accent-indigo-600 h-1 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer"
                        />
                        <ZoomIn size={16} className="text-slate-500" />
                    </div>
                    <div className="flex items-center gap-3 flex-1 border-l border-slate-200 dark:border-white/10 pl-6">
                        <RotateCw size={16} className="text-slate-500" />
                        <input
                            type="range"
                            value={rotation}
                            min={0}
                            max={360}
                            step={1}
                            aria-labelledby="Rotation"
                            onChange={(e) => setRotation(e.target.value)}
                            className="flex-1 accent-indigo-600 h-1 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                </div>

                <div className="flex justify-center gap-4 pt-2">
                    <button
                        onClick={onCancel}
                        className="px-8 py-3 rounded-xl border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 font-bold hover:bg-slate-100 dark:hover:bg-white/5 transition-all flex items-center gap-2"
                    >
                        <X size={18} /> Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-8 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-500/20 flex items-center gap-2"
                    >
                        <Check size={18} /> Apply Crop
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ImageCropper
